"use client";
import { cn } from "@/lib/utils";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { SideBarAuthModalProvider } from "@/context/SideBarAuthModal";
import SidebarAuthModal from "@/components/views/modals/sidebar/SidebarAuthModal";
import { SidebarContent } from "@/components/views/modals/sidebar/SidebarContent";

interface Props {
  show: boolean;
  onClose: () => void;
}

export default function SideBarMenu(props: Props) {
  const [sidebarStyle, setSidebarStyle] = useState<string>(
    "w-[0px] -z-10 opacity-0"
  );
  const container = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRenderListener = useRef(true);

  useEffect(() => {
    if (props.show) {
      setSidebarStyle("container w-full sm:w-[414px] z-[99] opacity-100");
    } else {
      setSidebarStyle("w-[0px] -z-10 opacity-0");
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [props.show]);

  useEffect(() => {
    if (isFirstRenderListener.current) {
      isFirstRenderListener.current = false;
      return;
    }

    const listener = (e: MouseEvent) => {
      if (!container.current?.contains(e.target as Node) && props.show) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
    };
  }, [props.show]);

  // Lock the page behind the menu so opening it on mobile doesn't scroll the page.
  useEffect(() => {
    if (!props.show) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [props.show]);

  const handleClose = () => {
    setSidebarStyle("w-[0px] opacity-0 overflow-y-hidden");
    timeoutRef.current = setTimeout(() => {
      props.onClose();
    }, 300);
  };

  const overlay = (
    <div className="fixed w-full bg-primary/30 h-[100dvh] right-0 top-0 flex justify-center z-[9999]">
      <section className="relative left-0 w-full">
        <aside
          ref={container}
          className={cn(
            "absolute right-0 overflow-y-auto overscroll-contain overflow-x-hidden h-full duration-300 bg-white",
            sidebarStyle,
          )}
        >
          <div className="h-full md:px-4">
            <SidebarContent onClose={handleClose} />
          </div>
        </aside>
      </section>
    </div>
  );

  return (
    <SideBarAuthModalProvider>
      {/* Rendered into <body> so the menu isn't trapped in the header's stacking
          context and covers the mobile bottom bar instead of sliding under it.
          `show` only becomes true after a click, so `document` is always there. */}
      {props.show ? createPortal(overlay, document.body) : null}

      <SidebarAuthModal />
    </SideBarAuthModalProvider>
  );
}
